import { useState, useRef, useCallback } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "@context/ThemeContext";
import { useNotification } from "@context/NotificationContext";
import { useAppDispatch } from "@/store";
import { uploadFiles } from "@/store/filesSlice";
import { MAX_FILES_PER_UPLOAD } from "@/constants/app";

export function FileUpload() {
  const { t } = useTranslation();
  const { mode } = useThemeMode();
  const { notify } = useNotification();
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      if (files.length > MAX_FILES_PER_UPLOAD) {
        notify(t("upload.maxFiles", { count: MAX_FILES_PER_UPLOAD }), "error");
        return;
      }

      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("files", files[i]);
      }

      setUploading(true);
      try {
        await dispatch(uploadFiles(formData)).unwrap();
        notify(t("upload.success"), "success");
      } catch {
        notify(t("upload.failed"), "error");
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [dispatch, notify, t],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      sx={{
        bgcolor: "background.paper",
        borderRadius: 4,
        p: 4,
        border: "2px dashed",
        borderColor: dragOver ? "secondary.main" : mode === "light" ? "#D1D5DB" : "#444",
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: "secondary.main",
          "& .upload-icon": { transform: "scale(1.1)" },
        },
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />
      {uploading ? (
        <CircularProgress sx={{ my: 2 }} />
      ) : (
        <>
          <Box
            className="upload-icon"
            sx={{
              width: 80,
              height: 80,
              bgcolor: mode === "light" ? "#EFF6FF" : "#1E293B",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
              transition: "transform 0.2s",
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 36, color: "secondary.main" }} />
          </Box>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("upload.dragDrop")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 3,
            }}
          >
            {t("upload.browse")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              color: "text.disabled",
              fontSize: 12,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PictureAsPdfIcon sx={{ fontSize: 16 }} /> PDF
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <ImageIcon sx={{ fontSize: 16 }} /> Images
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <FolderZipIcon sx={{ fontSize: 16 }} /> ZIP
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
              }}
            >
              Max 50MB per file
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}
